import {
  BackendAgentMessage,
  ChatJessWebSocketEvent,
  ChatJessMessage,
  SendMessageToService,
  ChatItem,
  RESPONSE_TYPES,
  HumanRequestInfo,
} from "../../pages/Dashboard/ChatJess/types";
import { logger } from "../../utils/logger";

/**
 * Transforms backend AgentMessage to frontend ChatJessWebSocketEvent
 * This middleware handles the mapping between backend and frontend message formats
 */
export function transformBackendMessage(
  backendMessage: BackendAgentMessage,
): ChatJessWebSocketEvent | null {
  switch (backendMessage.requestType) {
    case RESPONSE_TYPES.MESSAGE_RESPONSE:
      return transformMessageResponse(
        backendMessage.payload as SendMessageToService,
      );

    case RESPONSE_TYPES.CHAT_HISTORY:
      return transformChatHistory(backendMessage.payload as ChatItem[]);

    case RESPONSE_TYPES.REQUEST_HUMAN_SUPPORT:
      return transformHumanSupport(backendMessage.payload);

    case RESPONSE_TYPES.HUMAN_QA_TEAM_MESSAGE_UPDATE:
      return transformHumanQATeamMessageUpdate(
        backendMessage.payload as ChatItem,
      );

    case RESPONSE_TYPES.KPI_DASHBOARD_UPDATE:
      return {
        type: "kpiDashboardUpdate",
        timestamp: new Date().toISOString(),
        data: backendMessage.payload,
      } as any;

    default:
      logger.warn(
        "[Transform] Unknown requestType:",
        backendMessage.requestType,
      );
      return null;
  }
}

/**
 * Transform messageResponse to frontend message event
 * Maps SendMessageToService to a message that looks like old "mensagem_ia"
 */
function transformMessageResponse(
  payload: SendMessageToService,
): ChatJessWebSocketEvent {
  // Extract the message content from data.message (messageResponse format)
  const messageContent = payload.data?.message || payload.data?.body || "";
  const hasFeedback = payload.data?.feedback !== undefined;

  // Process media attachments and detect audio
  let attachments: any[] = [];
  let audioData: any = undefined;
  let isAudioMessage = false;
  let messageType: "text" | "file" | "mixed" | "audio" = payload.hasMedia
    ? "file"
    : "text";

  if (payload.hasMedia && payload.media && payload.media.length > 0) {
    attachments = payload.media.map((media, index) => {
      const attachment = {
        id: `${payload.messageID}-media-${index}`,
        name: media.filename || "attachment",
        size: media.filesize || 0,
        type: media.mimetype,
        url: media.url,
      };

      // For images, set thumbnail
      if (media.mimetype && media.mimetype.startsWith("image/")) {
        (attachment as any).thumbnail = media.url;
      }

      return attachment;
    });

    // Check if first media is audio
    const firstMedia = payload.media[0];
    if (
      firstMedia &&
      firstMedia.mimetype &&
      firstMedia.mimetype.startsWith("audio/")
    ) {
      isAudioMessage = true;
      messageType = "audio";

      audioData = {
        url: firstMedia.url,
        blob: undefined,
        base64: "",
        duration: 0,
        mimeType: firstMedia.mimetype,
        waveformData: undefined,
      };
    }
  }

  // Create a ChatJessMessage from SendMessageToService
  const message: ChatJessMessage = {
    id: payload.messageID,
    content: messageContent,
    author: payload.originsIA ? "jess" : "human",
    timestamp: new Date(),
    messageType,
    // If there's feedback in the data, this is a feedback request
    hasFeedback: hasFeedback,
    feedbackId: hasFeedback ? payload.messageID : undefined,
    supportType: hasFeedback ? "requiresHumanAnswer" : undefined,
    // Handle media attachments
    attachments: attachments.length > 0 ? attachments : undefined,
    audioData,
    isAudioMessage,
  };

  // If this has feedback, return as feedback_request
  if (hasFeedback) {
    return {
      type: "feedback_request",
      timestamp: new Date().toISOString(),
      data: {
        message: {
          ...message,
          feedbackTitle: "Preciso de orientação",
          feedbackDetails: payload.data?.feedback?.question || messageContent,
          supportMessages: payload.data?.feedback
            ? [
                {
                  question: payload.data.feedback.question,
                  answer: payload.data.feedback.answer,
                  media: payload.data.feedback.media,
                },
              ]
            : [],
        },
      },
    } as any;
  }

  // Otherwise return as normal message
  return {
    type: "message",
    timestamp: new Date().toISOString(),
    data: {
      message,
    },
  } as any;
}

/**
 * Transform chatHistory to frontend event
 * Already mostly compatible, just need to wrap it
 */
function transformChatHistory(payload: ChatItem[]): ChatJessWebSocketEvent {
  return {
    type: "chatHistory",
    timestamp: new Date().toISOString(),
    data: {
      items: payload,
    },
  } as any;
}

function buildConversationFlow(
  team_messages: any[],
): { question: string; answer?: string; media?: any; answerMedia?: any[] }[] {
  const conversationFlow: {
    question: string;
    answer?: string;
    media?: any;
    answerMedia?: any[];
  }[] = [];

  const sortedMessages = [...team_messages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  let currentQuestion: string | null = null;
  let currentMedia: any = null;

  for (const teamMsg of sortedMessages) {
    if (teamMsg.who === "assistant") {
      if (currentQuestion !== null) {
        conversationFlow.push({
          question: currentQuestion,
          answer: undefined,
          media: currentMedia,
        });
      }
      currentQuestion = teamMsg.message || teamMsg.body;
      currentMedia =
        teamMsg.media && teamMsg.media.length > 0
          ? {
              url: teamMsg.media[0].url,
              mimetype: teamMsg.media[0].mime_type,
              filename:
                teamMsg.media[0].metadata === "file"
                  ? teamMsg.media[0].url.split("/").pop()
                  : undefined,
            }
          : null;
    } else if (teamMsg.who === "user") {
      if (currentQuestion !== null) {
        const userMedia =
          teamMsg.media &&
          Array.isArray(teamMsg.media) &&
          teamMsg.media.length > 0
            ? teamMsg.media.map((m: any) => ({
                url: m.url,
                mimetype: m.mime_type || m.mimetype,
                filename: m.filename,
                filesize: m.filesize,
              }))
            : undefined;

        conversationFlow.push({
          question: currentQuestion,
          answer: teamMsg.message || teamMsg.body,
          media: currentMedia,
          answerMedia: userMedia,
        });
        currentQuestion = null;
        currentMedia = null;
      }
    }
  }

  if (currentQuestion !== null) {
    conversationFlow.push({
      question: currentQuestion,
      answer: undefined,
      media: currentMedia,
    });
  }

  return conversationFlow;
}

function buildHumanSupportMessage(humanRequestInfo: any): ChatJessMessage {
  const conversationFlow =
    humanRequestInfo.team_messages &&
    Array.isArray(humanRequestInfo.team_messages)
      ? buildConversationFlow(humanRequestInfo.team_messages)
      : [];

  return {
    id: humanRequestInfo.id || `hr-${Date.now()}`,
    content:
      humanRequestInfo.message || "Preciso de ajuda com esta solicitação",
    author: "jess",
    timestamp: new Date(humanRequestInfo.created_at || new Date()),
    feedbackId: `${humanRequestInfo.human_qa_id}`,
    hasFeedback: true,
    messageType: "text",
    supportType: humanRequestInfo.humanSupportType || "requiresHumanApproval",
    isCompleted: humanRequestInfo.isCompleted || false,
    stepId: humanRequestInfo.stepId,
    supportMessages: conversationFlow,
  };
}

function transformHumanSupport(payload: any): ChatJessWebSocketEvent {
  const humanRequestInfo = payload.payload || payload;
  const message = buildHumanSupportMessage(humanRequestInfo);

  return {
    type: "feedback_request",
    timestamp: new Date().toISOString(),
    data: {
      message,
    },
  } as any;
}

function transformHumanQATeamMessageUpdate(
  payload: ChatItem,
): ChatJessWebSocketEvent {
  // Extract HumanRequestInfo from ChatItem payload
  const humanRequestInfo = payload.payload as HumanRequestInfo;
  const message = buildHumanSupportMessage(humanRequestInfo);

  return {
    type: "humanQATeamMessageUpdate",
    timestamp: new Date().toISOString(),
    data: {
      message,
    },
  } as any;
}
